'use client';

import type { TGlobalNavigation } from '@chemist4u/newt-components';
import { GlobalNavigation } from '@chemist4u/newt-components';
import { useSearchContext } from '@lib/context/search-context';
import { useAuth } from '@modules/common/components/auth-provider';
import { useCart } from '@modules/common/components/cart-provider';
import BasketPreview from '@modules/layout/components/basket-preview';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

type GlobalNavigationClientProps = Omit<TGlobalNavigation, 'search'> & {
  search?: {
    placeholder?: string;
    ariaLabel?: string;
    legend?: string;
  };
};

export default function GlobalNavigationClient({
  search: staticSearch = {},
  basket,
  myAccount,
  ...navProps
}: GlobalNavigationClientProps) {
  const { query, results, setQuery, isOpen, recentSearches } =
    useSearchContext();
  const router = useRouter();
  const pathname = usePathname();
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  const { snapshot, mutationVersion } = useCart();
  const { isLoggedIn } = useAuth();
  const cart = snapshot.cart;
  const [autoOpenSignal, setAutoOpenSignal] = useState(mutationVersion);

  // Pop the basket preview open after the user adds/updates/removes an
  // item, unless they're already looking at the full basket page.
  // `pathname` is read via a ref (not a dependency) so a plain navigation
  // with no new mutation never re-triggers this — only a genuine cart
  // change should.
  useEffect(() => {
    if (!pathnameRef.current.startsWith('/basket')) {
      setAutoOpenSignal(mutationVersion);
    }
  }, [mutationVersion]);

  return (
    <GlobalNavigation
      {...navProps}
      basket={
        basket
          ? {
              ...basket,
              count: cart?.items?.reduce((acc, item) => acc + item.quantity, 0),
              autoOpenSignal,
              renderPreview: (close) => (
                <BasketPreview cart={cart} onRequestClose={close} />
              ),
            }
          : basket
      }
      myAccount={myAccount ? { ...myAccount, isLoggedIn } : myAccount}
      search={{
        placeholder: staticSearch.placeholder,
        ariaLabel: staticSearch.ariaLabel,
        legend: staticSearch.legend,
        query,
        results,
        setQuery,
        showRecentOnFocus: isOpen,
        recentSearches: {
          items: recentSearches,
        },
        onProductClick: (handle) => {
          router.push(`/${handle}`);
        },
        handleSubmit: () => {
          router.push(`/search?q=${encodeURIComponent(query)}`);
        },
      }}
    />
  );
}
